export const DEFAULT_EMAIL_SIGNATURE = "\nThank you,\nBuilder Playbook Team";
export const DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH = 296;
export const DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT = 148;

const EMAIL_SIGNATURE_KEY = "emailSignature";
const EMAIL_SIGNATURE_IMAGE_WIDTH_KEY = "emailSignatureImageWidth";
const EMAIL_SIGNATURE_IMAGE_HEIGHT_KEY = "emailSignatureImageHeight";

export const normalizeSignatureMarkup = (markup) => {
  if (!markup) {
    return "";
  }

  return String(markup)
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<!--([\s\S]*?)-->/g, "")
    .trim();
};

export const getSanitizedImageDimension = (value, fallbackValue) => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallbackValue;
};

export const applySignatureImageDimensions = (markup, widthValue, heightValue) => {
  if (!markup || !/<img[^>]*>/i.test(markup)) {
    return markup;
  }

  const width = getSanitizedImageDimension(widthValue, DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH);
  const height = getSanitizedImageDimension(heightValue, DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT);

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(markup, "text/html");
    const root = parsedDocument.body;

    root.querySelectorAll("img").forEach((image) => {
      image.setAttribute("width", String(width));
      image.setAttribute("height", String(height));
      image.style.width = `${width}px`;
      image.style.height = `${height}px`;
      image.style.maxWidth = "100%";
    });

    return root.innerHTML;
  } catch (error) {
    console.error("Signature image resize error:", error);
    return markup;
  }
};

export const replaceSignatureImageSource = (markup, nextSource, widthValue, heightValue) => {
  const normalizedMarkup = markup || "";
  const width = getSanitizedImageDimension(widthValue, DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH);
  const height = getSanitizedImageDimension(heightValue, DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT);

  if (!/<[^>]+>/.test(normalizedMarkup)) {
    return `<p><img src="${nextSource}" alt="signatureImage" width="${width}" height="${height}" style="width:${width}px;height:${height}px;max-width:100%;" /></p><p>${normalizedMarkup.replace(/\n/g, "<br/>")}</p>`;
  }

  const imagePattern = /<img([^>]*?)src=["'][^"']+["']([^>]*?)>/i;
  if (imagePattern.test(normalizedMarkup)) {
    const updatedMarkup = normalizedMarkup.replace(imagePattern, `<img$1src="${nextSource}"$2>`);
    return applySignatureImageDimensions(updatedMarkup, width, height);
  }

  return `${normalizedMarkup}<p><img src="${nextSource}" alt="signatureImage" width="${width}" height="${height}" style="width:${width}px;height:${height}px;max-width:100%;" /></p>`;
};

export const getStoredEmailSignatureSettings = () => {
  const fallback = {
    signature: DEFAULT_EMAIL_SIGNATURE,
    width: String(DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH),
    height: String(DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT),
  };

  if (typeof window === "undefined" || !window.localStorage) {
    return fallback;
  }

  return {
    signature: window.localStorage.getItem(EMAIL_SIGNATURE_KEY) || DEFAULT_EMAIL_SIGNATURE,
    width: String(
      getSanitizedImageDimension(
        window.localStorage.getItem(EMAIL_SIGNATURE_IMAGE_WIDTH_KEY),
        DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH,
      ),
    ),
    height: String(
      getSanitizedImageDimension(
        window.localStorage.getItem(EMAIL_SIGNATURE_IMAGE_HEIGHT_KEY),
        DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT,
      ),
    ),
  };
};

export const saveStoredEmailSignatureSettings = ({ signature, width, height }) => {
  const sanitizedWidth = getSanitizedImageDimension(width, DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH);
  const sanitizedHeight = getSanitizedImageDimension(height, DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT);
  const resizedSignature = applySignatureImageDimensions(
    signature || DEFAULT_EMAIL_SIGNATURE,
    sanitizedWidth,
    sanitizedHeight,
  ) || DEFAULT_EMAIL_SIGNATURE;

  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(EMAIL_SIGNATURE_KEY, resizedSignature);
    window.localStorage.setItem(EMAIL_SIGNATURE_IMAGE_WIDTH_KEY, String(sanitizedWidth));
    window.localStorage.setItem(EMAIL_SIGNATURE_IMAGE_HEIGHT_KEY, String(sanitizedHeight));
  }

  return {
    signature: resizedSignature,
    width: String(sanitizedWidth),
    height: String(sanitizedHeight),
  };
};

export const resetStoredEmailSignatureSettings = () => {
  return saveStoredEmailSignatureSettings({
    signature: DEFAULT_EMAIL_SIGNATURE,
    width: DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH,
    height: DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT,
  });
};

export const extractSignatureFromEmailBody = (emailBody) => {
  if (!emailBody) {
    return "";
  }

  let cleanedBody = normalizeSignatureMarkup(emailBody);

  const bodyMatch = cleanedBody.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) {
    cleanedBody = bodyMatch[1];
  }

  const signatureBlockMatch = cleanedBody.match(/<div[^>]*(class|id)="[^"]*(signature|x_signature)[^"]*"[^>]*>[\s\S]*?<\/div>/i);
  if (signatureBlockMatch) {
    return normalizeSignatureMarkup(signatureBlockMatch[0]);
  }

  const replySeparators = [
    /<div[^>]*class="gmail_quote"[^>]*>[\s\S]*$/i,
    /<div[^>]*id="divRplyFwdMsg"[^>]*>[\s\S]*$/i,
    /<hr[^>]*id="replySplit"[^>]*>[\s\S]*$/i,
    /<hr[^>]*>[\s\S]*$/i,
    /On\s.+?wrote:[\s\S]*$/i,
    /From:\s.+?Sent:\s.+/is,
  ];

  replySeparators.forEach((separator) => {
    cleanedBody = cleanedBody.replace(separator, "").trim();
  });

  const signatureMarkers = [
    "Thank you,",
    "Thanks,",
    "Best regards,",
    "Regards,",
    "Sincerely,",
    "Cheers,",
    "Kind regards,",
    "Sent from my",
  ];

  const lowerBody = cleanedBody.toLowerCase();
  let markerIndex = -1;

  signatureMarkers.forEach((marker) => {
    const index = lowerBody.lastIndexOf(marker.toLowerCase());
    if (index > markerIndex) {
      markerIndex = index;
    }
  });

  if (markerIndex >= 0) {
    return normalizeSignatureMarkup(cleanedBody.substring(markerIndex));
  }

  const plainTextLines = cleanedBody
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (plainTextLines.length === 0) {
    return "";
  }

  return plainTextLines.slice(Math.max(plainTextLines.length - 6, 0)).join("\n");
};